import { useGetUserFullNameWithFallback } from '@pba/react-utils';
import type { ChatRoom } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Button, Icon, Modal, Text } from 'ui';

import { ChatInfoModalContent } from '../../chat-info-modal-content';
import { ChatInfoProfile } from '../../chat-info-profile';
import { ChatGroupAddMembersModal } from './chat-group-add-members-modal';
import { ChatGroupInfoMembers } from './chat-group-info-members';
import { ChatGroupRenameModal } from './chat-group-rename-modal';
import { useChatGroupInfo, useRemoveChatMemberConfirmModal } from './hooks';

type P = {
  chatroom: ChatRoom;
  isOpened: boolean;
  onChatDeleted: () => Promise<void>;
  onClose: () => void;
};

const ChatGroupInfoModal = ({ chatroom, isOpened, onChatDeleted, onClose }: P) => {
  const { t } = useTranslation();
  const getUserFullNameWithFallback = useGetUserFullNameWithFallback();
  const { canRenameChat } = usePermissions();
  const { members, membersCount, name } = useChatGroupInfo(chatroom);
  const [isRenameModalOpened, setIsRenameModalOpened] = useState(false);
  const [isAddMembersModalOpened, setIsAddMembersModalOpened] = useState(false);
  const { confirm: confirmRemoveMember, contextHolder: removeMemberConfirmContextHolder } =
    useRemoveChatMemberConfirmModal({
      chatroomId: chatroom.id,
    });

  return (
    <>
      {removeMemberConfirmContextHolder}
      <Modal
        isOpened={isOpened}
        onCancel={onClose}
        title={
          <Text size="large" type="title" weight="medium">
            Chat info
          </Text>
        }
        width="medium"
      >
        <Modal.HugContent>
          <ChatInfoModalContent chatroom={chatroom} onChatDeleted={onChatDeleted} onClose={onClose}>
            <ChatInfoProfile
              actions={
                canRenameChat({ chatroom }) ? (
                  <Button
                    icon={<Icon icon="pencil-line" size="small" />}
                    onClick={() => {
                      setIsRenameModalOpened(true);
                    }}
                    type="vertical"
                  >
                    Rename
                  </Button>
                ) : undefined
              }
              description={t('membersCount', { count: membersCount })}
              icon={<Icon color="text" icon="group-line" />}
              name={name}
            />
            <ChatGroupInfoMembers
              chatroom={chatroom}
              members={members}
              onAddMember={() => {
                setIsAddMembersModalOpened(true);
              }}
              onRemoveMember={(member) => {
                confirmRemoveMember({
                  memberName: getUserFullNameWithFallback(member),
                  memberUserId: member.id,
                });
              }}
            />
          </ChatInfoModalContent>
        </Modal.HugContent>
      </Modal>
      <ChatGroupRenameModal
        chatroomId={chatroom.id}
        isOpened={isRenameModalOpened}
        name={chatroom.name}
        onCancel={() => {
          setIsRenameModalOpened(false);
        }}
      />
      <ChatGroupAddMembersModal
        chatroomId={chatroom.id}
        isOpened={isAddMembersModalOpened}
        onCancel={() => {
          setIsAddMembersModalOpened(false);
        }}
      />
    </>
  );
};

export default ChatGroupInfoModal;
